import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import GeneralInfoSection from "./GeneralInfoSection";
import PricingSection from "./PricingSection";
import InventorySection from "./InventorySection";
import ConfigurationSection from "./ConfigurationSection";
import ProductPreview from "./ProductPreview";
import { defaultProduct } from "../../../Models/ProductModel";
import { defaultService } from "../../../Models/ServiceModel";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { sampleProduct } from "../../../Models/ProductSample";
import { sampleService } from "../../../Models/ServiceSample";
import { fetchCompaniesByUserId } from "../../../db/Companies";
import ShippingSection from "./ShippingSection";

// ✅ Ajouts
import Button from "../../../components/controls/Button";
import PageHeader from "../../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../../images/AnanasSolutionsLogo.webp";
import DropdownMenu from "../../../components/controls/DropdownMenu";

const CreateEditProduct = () => {
  const { id } = useParams(); // "new" or existing ID
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [previewLang, setPreviewLang] = useState(i18n.language.split("-")[0]);
  const [priceTypesList, setPriceTypesList] = useState([]);
  const { currentUser } = useAuth();
  const isService = location.pathname.includes("/services");
  const [companies, setCompanies] = useState([]);
  const { t } = useTranslation();
  const [inventoryMode, setInventoryMode] = useState("unlimited");

  const [carriers, setCarriers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const queryParams = new URLSearchParams(location.search);
  const preselectedCompanyId = queryParams.get("companyId");

  const [formData, setFormData] = useState(() => {
    const base = isService ? defaultService : defaultProduct;

    return {
      ...base,
      // préselection compagnie si fournie
      companyId: preselectedCompanyId
        ? parseInt(preselectedCompanyId, 10)
        : base.companyId ?? null,
      hasShipping: base.hasShipping ?? true,
      ...(isService
        ? {}
        : {
            carrierAccountId: base.carrierAccountId ?? "",
            warehouseId: base.warehouseId ?? "",
            weightKg: Number(base.weightKg ?? 0),
            lengthCm: Number(base.lengthCm ?? 0),
            widthCm: Number(base.widthCm ?? 0),
            heightCm: Number(base.heightCm ?? 0),
            // (optionnel UI) unités par défaut
            weightUnit: base.weightUnit ?? "kg",
            lengthUnit: base.lengthUnit ?? "cm",
          }),
    };
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      inventoryMode === "contact" &&
      formData.productConfigurations?.length > 0
    ) {
      setFormData((prev) => ({
        ...prev,
        productConfigurations: [],
        carrierAccountId: data.carrierAccountId ?? "",
        warehouseId: data.warehouseId ?? "",
        weightKg: data.weightKg ?? 0,
        lengthCm: data.lengthCm ?? 0,
        widthCm: data.widthCm ?? 0,
        heightCm: data.heightCm ?? 0,
        hasShipping: data.hasShipping ?? true,
      }));
    }
  }, [inventoryMode]); // eslint-disable-line

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const base = process.env.REACT_APP_API_BASE_URL;
        const [carRes, whRes] = await Promise.all([
          fetch(`${base}/api/CarrierAccounts/my`),
          fetch(`${base}/api/Warehouses/my`),
        ]);
        const [carJson, whJson] = await Promise.all([
          carRes.json(),
          whRes.json(),
        ]);
        setCarriers(Array.isArray(carJson) ? carJson : []);
        setWarehouses(Array.isArray(whJson) ? whJson : []);
      } catch (e) {
        console.error("Lookup load failed", e);
      }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await fetchCompaniesByUserId(currentUser?.id);
        setCompanies(res);
        if (!formData.companyId && res.length > 0) {
          handleChange("companyId", res[0].id);
        }
      } catch (err) {
        console.error("Erreur chargement compagnies", err);
      }
    };
    if (currentUser?.id) loadCompanies();
  }, [currentUser?.id]);

  useEffect(() => {
    const loadPriceTypes = async () => {
      try {
        const base = process.env.REACT_APP_API_BASE_URL;
        const res = await fetch(`${base}/api/PriceTypes`);
        const data = await res.json();

        setPriceTypesList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erreur chargement price types", err);
      }
    };

    loadPriceTypes();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id === "testdamayaya") {
          setFormData(isService ? sampleService : sampleProduct);
        } else if (id && id !== "new") {
          const url = `${process.env.REACT_APP_API_BASE_URL}/api/${
            isService ? "Services" : "Products"
          }/${id}`;
          const res = await fetch(url);
          const data = await res.json();
          setFormData(data);
        } else {
          // Valeurs par défaut
          setFormData((prev) => ({
            ...prev,
            productInventoryLimits: {
              inventoryMode: "fixed",
              totalQuantity: 0,
              maxDuration: 0,
              returnMode: "none",
            },
            ...(isService ? {} : { productConfigurations: [] }),
          }));
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [id, isService]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onTempImageChange = (url) => {
    setFormData((prev) => ({
      ...prev,
      tempImageUrl: url,
    }));
  };

  const handleNestedChange = (field, subfield, value) => {
    setFormData((prev) => {
      if (subfield === null) {
        return { ...prev, [field]: value };
      }
      return {
        ...prev,
        [field]: {
          ...(prev[field] || {}),
          [subfield]: value,
        },
      };
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (!formData.priceTypeId || formData.priceTypeId === "0") {
        alert("Vous devez sélectionner un type de prix.");
        setLoading(false);
        return;
      }
      if (
        formData.price === null ||
        formData.price === "" ||
        isNaN(formData.price)
      ) {
        alert("Vous devez entrer un prix valide.");
        setLoading(false);
        return;
      }
      if (parseInt(formData.priceTypeId, 10) !== 4) {
        formData.productConfigurations = [];
      }
      console.log(formData.hasShipping);
      if (!isService && formData.hasShipping) {
        if (!formData.carrierAccountId) {
          alert("Sélectionnez un transporteur.");
          setLoading(false);
          return;
        }
        if (!formData.warehouseId) {
          alert("Sélectionnez un entrepôt d’envoi.");
          setLoading(false);
          return;
        }
        if (!formData.weightKg || formData.weightKg <= 0) {
          alert("Entrez un poids valide.");
          setLoading(false);
          return;
        }
      } else {
        formData.carrierAccountId = null;
        formData.warehouseId = null;
      }

      const priceTypeId = parseInt(formData.priceTypeId, 10);
      const inventoryMode = formData.productInventoryLimits?.inventoryMode;

      const cleanedFormData = {
        ...formData,
        userId: currentUser?.id,
        imageUrl: formData.tempImageUrl || formData.imageUrl,
        price: parseFloat(formData.price?.toString().replace(",", ".")) || 0,
        priceTypeId,
        hasShipping: formData.hasShipping !== false,
        productInventoryLimits: {
          ...formData.productInventoryLimits,
          totalQuantity:
            parseInt(formData.productInventoryLimits.totalQuantity) || 0,
        },

        companyId: formData.companyId || null,
        ...(isService
          ? {}
          : {
              productConfigurations:
                formData.productConfigurations?.map((option) => ({
                  ...option,
                  values: option.values.map((value) => ({
                    ...value,
                    price:
                      Number(
                        parseFloat(value.price?.toString().replace(",", "."))
                      ) || 0,
                    quantity: value.quantity ?? 0,
                  })),
                })) || [],
            }),
      };

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/${
        isService ? "Services" : "Products"
      }/WithAvailabilities`;

      const method = "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanedFormData),
      });

      if (!response.ok) throw new Error("Save failed");

      navigate(
        isService
          ? "/provider-dashboard-service-list"
          : "/provider-dashboard-product-list"
      );
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setLoading(false);
    }
  };

  // --------- TITRE HEADER ----------
  const headerTitle =
    id && id !== "new"
      ? isService
        ? t("services.editService", "Modifier le service")
        : t("products.editProduct", "Modifier le produit")
      : isService
      ? t("services.newService", "Nouveau service")
      : t("products.newProduct", "Nouveau produit");

  const headerSubtitle = isService
    ? t("services.headerSubtitle", "Renseignez les infos de votre service")
    : t("products.headerSubtitle", "Renseignez les infos de votre produit");

  return (
    <div className="flex flex-col min-h-screen bg-background text-onWhite overflow-x-hidden">
      {/* ✅ Page Header plein écran */}
      <PageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed
        rounded={false}
        height="sm"
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      <div className="flex flex-col lg:flex-row flex-1">
        {/* ------- Colonne gauche (form) ------- */}
        <div className="w-full lg:w-2/3 p-4 md:p-6 space-y-8">
          <div className="w-full max-w-sm mb-6 mx-4">
            <label className="block text-sm font-medium text-onWhite mb-1">
              {t("products.selectCompany")}
            </label>
            <DropdownMenu
              title={
                companies.find((c) => c.id === formData.companyId)?.name ||
                "Sélectionner une compagnie"
              }
              isDropdown={true}
              items={companies.map((company) => ({
                label: company.name,
                onClick: () => handleChange("companyId", company.id),
              }))}
              onClick={null}
            />
          </div>

          <GeneralInfoSection
            formData={formData}
            onChange={handleChange}
            onNestedChange={handleNestedChange}
            onTempImageChange={onTempImageChange}
            isService={isService}
          />

          {!isService && (
            <ShippingSection formData={formData} onChange={handleChange} />
          )}

          <PricingSection
            formData={formData}
            onChange={handleChange}
            priceTypesList={priceTypesList}
            isService={isService}
          />

          {/*<InventorySection
            formData={formData}
            priceTypeId={formData.priceTypeId}
            onChange={(subfield, value) =>
              handleNestedChange("productInventoryLimits", subfield, value)
            }
            isService={isService}
            onInventoryModeChange={setInventoryMode}
          />*/}

          {/* ✅ Bouton flottant “Enregistrer” (remplace bg-bgCyan etc.) */}
          <div className="text-right">
            <Button
              as="button"
              type="button"
              onClick={handleSave}
              color="primary"
              size="lg"
              disabled={loading}
              leftIcon={<CheckIcon className="w-6 h-6" />}
              className="fixed bottom-8 right-8 z-50 text-lg font-semibold"
            >
              {loading
                ? t("global.saving", "Sauvegarde...")
                : t("global.save", "Enregistrer")}
            </Button>
          </div>
        </div>

        {/* ------- Colonne droite (preview) ------- */}
        <div className="w-full lg:w-1/3">
          <div className="sticky top-6">
            <div className="bg-white shadow-md border-l p-6 max-h overflow-y-auto rounded-lg mt-6">
              <div className="flex justify-start items-center mb-4">
                <h3 className="text-lg font-bold text-primary">Aperçu</h3>
                <div className="flex gap-2 text-sm ml-4">
                  <Button
                    as="button"
                    type="button"
                    onClick={() => setPreviewLang("fr")}
                    size="sm"
                    color={previewLang === "fr" ? "mint" : "primary"}
                    className="px-3 py-1"
                  >
                    FR
                  </Button>
                  <Button
                    as="button"
                    type="button"
                    onClick={() => setPreviewLang("en")}
                    size="sm"
                    color={previewLang === "en" ? "mint" : "primary"}
                    className="px-3 py-1"
                  >
                    EN
                  </Button>
                </div>
              </div>

              <ProductPreview
                key={previewLang}
                lang={previewLang}
                priceTypesList={priceTypesList}
                formData={{
                  ...formData,
                  company:
                    companies.find((c) => c.id === formData.companyId) || null,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEditProduct;
